/**
 * 图片本地缓存工具（IndexedDB）
 * 策略：首次访问图片正常加载不影响展示，后台将 OSS 压缩版下载写入本地缓存；
 *       之后刷新页面命中缓存，直接使用本地图片，不再走网络。
 */
const DB_NAME = 'yxz-img-cache'
const STORE_NAME = 'images'
const DB_VERSION = 1
const CACHE_MAX_DAYS = 15 // 缓存有效期（天）
const CACHE_MAX_COUNT = 60 // 最大缓存条数

let dbPromise = null

function openDB() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('indexedDB not supported'))
      return
    }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      dbPromise = null
      reject(req.error)
    }
  })
  return dbPromise
}

function txGet(db, key) {
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key)
      req.onsuccess = () => resolve(req.result || null)
      req.onerror = () => resolve(null)
    } catch (e) {
      resolve(null)
    }
  })
}

function txPut(db, record) {
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put(record)
      req.onsuccess = () => resolve()
      req.onerror = () => resolve()
    } catch (e) {
      resolve()
    }
  })
}

// 清理过期/超限缓存
function cleanup(db) {
  try {
    const store = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME)
    const req = store.getAll()
    req.onsuccess = () => {
      let records = req.result || []
      const expireBefore = Date.now() - CACHE_MAX_DAYS * 24 * 60 * 60 * 1000
      records.forEach(r => {
        if (!r.time || r.time < expireBefore) store.delete(r.key)
      })
      records = records.filter(r => r.time && r.time >= expireBefore)
      if (records.length > CACHE_MAX_COUNT) {
        records.sort((a, b) => a.time - b.time)
        records.slice(0, records.length - CACHE_MAX_COUNT).forEach(r => store.delete(r.key))
      }
    }
  } catch (e) { /* 清理失败不影响主流程 */ }
}

// OSS 图片压缩：限制宽度并降低质量，缓存与展示都用小图，减少体积
function optimizeUrl(url) {
  if (!url || url.indexOf('aliyuncs.com') === -1) return url
  if (url.indexOf('x-oss-process') !== -1) return url
  return url + (url.indexOf('?') === -1 ? '?' : '&') + 'x-oss-process=image/resize,w_1200/quality,q_80'
}

/**
 * 获取图片可用地址：命中本地缓存返回 blob: 地址（秒开），否则返回压缩后的原图地址
 * 同步语义返回 Promise，永不 reject
 * @param {string} url 原始图片地址
 * @returns {Promise<string>}
 */
export function getImgSrc(url) {
  const realUrl = optimizeUrl(url)
  return new Promise((resolve) => {
    if (!realUrl || !/^https?:\/\//.test(realUrl)) {
      resolve(realUrl)
      return
    }
    openDB()
      .then(db => {
        cleanup(db)
        return txGet(db, realUrl)
      })
      .then(record => {
        resolve(record && record.blob ? URL.createObjectURL(record.blob) : realUrl)
      })
      .catch(() => resolve(realUrl))
  })
}

/**
 * 后台缓存图片（fire-and-forget）：下载 OSS 压缩图写入 IndexedDB，下次刷新秒开
 * @param {string} url 原始图片地址
 */
export function cacheImg(url) {
  const realUrl = optimizeUrl(url)
  if (!realUrl || !/^https?:\/\//.test(realUrl)) return
  openDB()
    .then(db => txGet(db, realUrl).then(record => {
      if (record && record.blob) return // 已有缓存
      return fetch(realUrl, { mode: 'cors' })
        .then(res => {
          if (!res.ok) throw new Error('http ' + res.status)
          return res.blob()
        })
        .then(blob => {
          if (!/^image\//.test(blob.type)) throw new Error('not image')
          return txPut(db, { key: realUrl, blob: blob, time: Date.now() })
        })
        .catch(() => { /* CORS 或网络失败，静默跳过 */ })
    }))
    .catch(() => {})
}



// WEBPACK FOOTER //
// ./src/utils/imgCache.js